
import { Injectable } from '@angular/core';
import { AuthService } from '../../core/services/authentication.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as firebase from 'firebase/app';
import 'firebase/messaging';
import { NotificationsClientService } from './notifications-client.service';
import { environment } from 'src/environments/environment';
import {
    RegisterFcmSubscriptionRequest,
    RegisterFcmSubscriptionResponse,
    TopicSubscriptionResult,
    UnregisterFcmSubscriptionResponse
} from '../interfaces/notifications-client';

export type BrowserNotificationPermissionState = NotificationPermission | 'unsupported';
export type BrowserNotificationAvailability = 'available' | 'unconfigured' | 'unsupported';

@Injectable()
export class MessagingService {

    private foregroundMessageSubject = new Subject<any>();
    public foregroundMessage$ = this.foregroundMessageSubject.asObservable();
    private soundEnabledSubject: BehaviorSubject<boolean>;
    public soundEnabled$: Observable<boolean>;
    private consoleMessageUnsubscribe: () => void;
    private serviceWorkerMessageListenerAttached = false;
    private tokenRegistrationActive = false;
    private currentFcmToken: string;
    private currentFcmSubscriptionPayload: RegisterFcmSubscriptionRequest;
    private registeredFcmSubscriptionKey: string;
    private currentBrowserPushEndpoint: string;
    private currentNotificationsDeviceId: string;
    private notificationMessaging: firebase.messaging.Messaging;
    private firebaseMessagingServiceWorkerRegistration: Promise<ServiceWorkerRegistration> | null = null;
    private notificationAudio: HTMLAudioElement | null = null;
    private notificationAudioUnlocked = false;
    private soundUnlockListenersAttached = false;
    private soundUnlockInProgress: Promise<boolean>;
    private notificationsConfigurationWarningLogged = false;
    private readonly notificationsFirebaseAppName = 'notifications';
    private readonly notificationsDeviceIdStorageKey = 'notificationsDeviceId';
    private readonly firebaseMessagingSwMigrationStorageKey = 'firebaseMessagingSwMigrationVersion';
    private readonly firebaseMessagingSwMigrationVersion = '2026-05-20-fcm-scope-cleanup';
    private readonly firebaseMessagingServiceWorkerPath = '/firebase-messaging-sw.js';
    private readonly firebaseMessagingServiceWorkerScope = '/firebase-cloud-messaging-push-scope';
    private readonly serviceWorkerFcmTokenRecoveryMessageType = 'NOTIFICATIONS_REQUEST_FCM_TOKEN';
    private readonly notificationSoundPath = 'assets/sound/notification.mp3';
    private readonly notificationSoundEnabledStorageKey = 'notificationSoundEnabled';
    private readonly serviceWorkerNotificationMessageType = 'NOTIFICATIONS_PUSH_RECEIVED';
    private readonly soundUnlockEvents: string[] = ['click', 'keydown', 'touchstart'];
    private soundUnlockHandler = () => this.unlockNotificationSound();
    private lastHandledNotificationKey: string;
    private lastHandledNotificationAt = 0;
    private fcmRegisterRetryTimeoutId: any = null;
    private fcmTokenRecoveryRetryTimeoutId: any = null;
    private fcmTokenRecoveryAttempt = 0;
    private fcmIndexedDbClosingFailureCount = 0;
    private fcmTokenRecoveryPausedUntil = 0;
    private lastFcmTokenRecoveryError: any = null;
    private lastFcmSubscriptionRegisteredAt = 0;
    private readonly fcmRegisterMaxAttempts = 5;
    private readonly fcmRegisterBaseDelayMs = 2000;
    private readonly fcmRegisterMaxDelayMs = 60000;
    private readonly fcmTokenRecoveryMaxAttempts = 6;
    private readonly fcmTokenRecoveryBaseDelayMs = 2000;
    private readonly fcmTokenRecoveryMaxDelayMs = 30000;
    private readonly fcmIndexedDbClosingFailureThreshold = 2;
    private readonly fcmTokenRecoveryPauseMs = 5 * 60 * 1000;

    constructor(
        private authService: AuthService,
        private notificationsClient: NotificationsClientService
    ) {
        AuthService.fcmSubscriptionCleanup = () => this.unregisterCurrentUserFcmSubscription();
        this.soundEnabledSubject = new BehaviorSubject<boolean>(this.readSoundEnabledFromStorage());
        this.soundEnabled$ = this.soundEnabledSubject.asObservable();
        this.listenSoundPreferenceAcrossTabs();
        this.prepareNotificationSoundUnlock();
    }

    public listenForegroundMessagesInConsole() {
        this.listenServiceWorkerMessages();

        if (this.consoleMessageUnsubscribe) return;

        if (!this.isMessagingSupported()) {
            return;
        }

        const messaging = this.getNotificationMessaging();
        if (!messaging) return;

        this.consoleMessageUnsubscribe = messaging.onMessage(
            (payload) => this.handleIncomingNotificationPayload(payload)
        );
    }

    public isSoundEnabled(): boolean {
        return this.soundEnabledSubject.getValue();
    }

    public setSoundEnabled(enabled: boolean): void {
        if (this.soundEnabledSubject.getValue() === enabled) {
            if (enabled) this.prepareNotificationSoundUnlock();
            return;
        }

        try {
            localStorage.setItem(this.notificationSoundEnabledStorageKey, enabled ? 'true' : 'false');
        } catch (error) {
            // Storage may be unavailable; the in-memory preference remains usable.
        }
        this.soundEnabledSubject.next(enabled);

        if (enabled) {
            this.prepareNotificationSoundUnlock();
        } else {
            this.removeNotificationSoundUnlockListeners();
        }
    }

    private readSoundEnabledFromStorage(): boolean {
        try {
            const stored = localStorage.getItem(this.notificationSoundEnabledStorageKey);
            return stored === null ? true : stored === 'true';
        } catch (error) {
            return true;
        }
    }

    // El evento `storage` se dispara solo en las OTRAS pestañas del mismo origen,
    // no en la que ejecutó el setItem; por eso este listener solo refleja cambios
    // remotos y no necesita guardia para evitar loops.
    private listenSoundPreferenceAcrossTabs(): void {
        if (typeof window === 'undefined' || !window.addEventListener) return;

        window.addEventListener('storage', (event: StorageEvent) => {
            if (event.key !== this.notificationSoundEnabledStorageKey) return;

            const next = event.newValue === null ? true : event.newValue === 'true';
            if (this.soundEnabledSubject.getValue() !== next) {
                this.soundEnabledSubject.next(next);
                if (next) {
                    this.prepareNotificationSoundUnlock();
                } else {
                    this.removeNotificationSoundUnlockListeners();
                }
            }
        });
    }

    private listenServiceWorkerMessages(): void {
        if (this.serviceWorkerMessageListenerAttached) return;
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
            const message = event.data;
            if (!message || message.type !== this.serviceWorkerNotificationMessageType) return;

            const payload = message.payload && message.payload.rawPayload
                ? message.payload.rawPayload
                : message.payload;

            this.handleIncomingNotificationPayload(payload);
        });

        this.serviceWorkerMessageListenerAttached = true;
    }

    private handleIncomingNotificationPayload(payload: any): void {
        const key = this.buildNotificationPayloadKey(payload);
        const now = Date.now();

        if (key && this.lastHandledNotificationKey === key && now - this.lastHandledNotificationAt < 1500) {
            return;
        }

        this.lastHandledNotificationKey = key;
        this.lastHandledNotificationAt = now;

        this.foregroundMessageSubject.next(payload);
        this.playNotificationSound();
    }

    private buildNotificationPayloadKey(payload: any): string {
        const data = payload && payload.data ? payload.data : payload;

        if (!data) {
            return '';
        }

        return [
            data.batchId || '',
            data.notificationType || '',
            data.publishedAt || '',
            data.title || '',
            data.body || ''
        ].join('|');
    }

    private playNotificationSound(): void {
        if (!this.isSoundEnabled()) return;

        if (!this.notificationAudioUnlocked) {
            this.prepareNotificationSoundUnlock();
            return;
        }

        try {
            var audio = this.getNotificationAudio();
            if (!audio) return;

            audio.currentTime = 0;
            const playback = audio.play();
            if (playback && typeof playback.catch === 'function') {
                playback.catch((error) => {
                    this.notificationAudioUnlocked = false;
                    this.prepareNotificationSoundUnlock();
                });
            }
        } catch (error) {
            this.notificationAudioUnlocked = false;
            this.prepareNotificationSoundUnlock();
        }
    }

    private getNotificationAudio(): HTMLAudioElement | null {
        if (typeof Audio === 'undefined') return null;

        if (!this.notificationAudio) {
            this.notificationAudio = new Audio(this.notificationSoundPath);
            this.notificationAudio.preload = 'auto';
        }

        return this.notificationAudio;
    }

    private prepareNotificationSoundUnlock(): void {
        if (!this.isSoundEnabled() || this.notificationAudioUnlocked || this.soundUnlockListenersAttached) return;
        if (typeof document === 'undefined' || !document.addEventListener) return;

        this.soundUnlockEvents.forEach((eventName) => {
            document.addEventListener(eventName, this.soundUnlockHandler, true);
        });
        this.soundUnlockListenersAttached = true;
    }

    private removeNotificationSoundUnlockListeners(): void {
        if (!this.soundUnlockListenersAttached) return;
        if (typeof document === 'undefined' || !document.removeEventListener) return;

        this.soundUnlockEvents.forEach((eventName) => {
            document.removeEventListener(eventName, this.soundUnlockHandler, true);
        });
        this.soundUnlockListenersAttached = false;
    }

    private async performNotificationSoundUnlock(): Promise<boolean> {
        var audio = this.getNotificationAudio();
        if (!audio) return false;

        var previousVolume = audio.volume;
        var previousMuted = audio.muted;

        try {
            audio.volume = 0;
            audio.muted = true;
            audio.currentTime = 0;

            var playback = audio.play();
            if (playback && typeof playback.then === 'function') {
                await playback;
            }

            audio.pause();
            audio.currentTime = 0;
            this.notificationAudioUnlocked = true;
            this.removeNotificationSoundUnlockListeners();

            return true;
        } catch (error) {
            return false;
        } finally {
            audio.volume = previousVolume;
            audio.muted = previousMuted;
        }
    }

    public async enableNotificationAlerts(): Promise<BrowserNotificationPermissionState> {
        this.setSoundEnabled(true);
        var soundUnlock = this.unlockNotificationSound();
        var permission = await this.requestBrowserNotificationPermission();

        await soundUnlock;

        return permission;
    }

    public registerCurrentUserFcmSubscription(requestBrowserPermission: boolean = true) {
        if (this.tokenRegistrationActive) return;

        if (!this.isNotificationsConfigured()) return;

        if (!this.isMessagingSupported()) {
            return;
        }

        if (!this.authService || !this.authService.sessionActive()) {
            return;
        }

        const user = this.authService.getUserSession();
        const document = user && user.information ? user.information.document : null;
        const companyId = user ? user.clientCompanyId : null;
        const roleId = user ? user.roleId : null;
        if (!document || !companyId || !roleId) {
            console.warn('[Notifications] Missing user data for FCM subscription', { companyId, document, roleId });
            return;
        }

        if (!this.canAttemptFcmSubscription(requestBrowserPermission)) return;

        this.tokenRegistrationActive = true;

        this.getMessagingToken(requestBrowserPermission).then((token) => {
                if (!token) {
                    this.tokenRegistrationActive = false;
                    this.scheduleFcmTokenRecoveryRetry('bootstrap-token-unavailable');
                    return;
                }

                this.registerResolvedFcmTokenWithBackend(token);
            });
    }

    public getNotificationPermissionState(): BrowserNotificationPermissionState {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            return 'unsupported';
        }

        return Notification.permission;
    }

    public async requestBrowserNotificationPermission(): Promise<BrowserNotificationPermissionState> {
        if (this.getBrowserNotificationAvailability() !== 'available') {
            return this.getNotificationPermissionState();
        }

        return this.resolveNotificationPermission(true);
    }

    public unlockNotificationSound(): Promise<boolean> {
        if (!this.isSoundEnabled()) return Promise.resolve(false);
        if (this.notificationAudioUnlocked) return Promise.resolve(true);
        if (this.soundUnlockInProgress) return this.soundUnlockInProgress;

        this.soundUnlockInProgress = this.performNotificationSoundUnlock().then(
            (unlocked) => {
                this.soundUnlockInProgress = null;
                return unlocked;
            },
            () => {
                this.soundUnlockInProgress = null;
                return false;
            }
        );

        return this.soundUnlockInProgress;
    }

    public getBrowserNotificationAvailability(): BrowserNotificationAvailability {
        const settings = this.getNotificationsSettings();

        if (!settings ||
            settings.enabled === false ||
            !settings.serverUrl ||
            !settings.productId ||
            !settings.firebaseConfig ||
            !settings.vapidKey) {
            return 'unconfigured';
        }

        return this.isMessagingSupported() ? 'available' : 'unsupported';
    }

    public async unregisterCurrentUserFcmSubscription(): Promise<void> {
        this.stopCurrentUserFcmSubscriptionRegistration();

        const payload = await this.getCurrentFcmSubscriptionPayload();
        if (!payload) {
            return;
        }

        const response = await this.notificationsClient.unregisterFcmSubscription(payload).toPromise();
        this.logFcmUnregistrationResult(response);
        this.currentFcmSubscriptionPayload = null;
        this.currentFcmToken = null;
        this.registeredFcmSubscriptionKey = null;
        this.currentBrowserPushEndpoint = null;
    }

    public stopForegroundMessagesInConsole() {
        if (this.consoleMessageUnsubscribe) {
            this.consoleMessageUnsubscribe();
            this.consoleMessageUnsubscribe = null;
        }
    }

    public stopCurrentUserFcmSubscriptionRegistration() {
        if (this.fcmRegisterRetryTimeoutId) {
            clearTimeout(this.fcmRegisterRetryTimeoutId);
            this.fcmRegisterRetryTimeoutId = null;
        }
        if (this.fcmTokenRecoveryRetryTimeoutId) {
            clearTimeout(this.fcmTokenRecoveryRetryTimeoutId);
            this.fcmTokenRecoveryRetryTimeoutId = null;
        }
        this.fcmTokenRecoveryAttempt = 0;
        this.fcmIndexedDbClosingFailureCount = 0;
        this.fcmTokenRecoveryPausedUntil = 0;
        this.lastFcmTokenRecoveryError = null;
        this.tokenRegistrationActive = false;
    }

    private isMessagingSupported(): boolean {
        try {
            return 'Notification' in window &&
                'serviceWorker' in navigator &&
                'PushManager' in window &&
                firebase.messaging.isSupported();
        } catch (error) {
            return false;
        }
    }

    private isNotificationsConfigured(): boolean {
        const settings = this.getNotificationsSettings();

        if (!settings || settings.enabled === false) return false;

        if (!settings.serverUrl) {
            this.warnNotificationsConfigurationOnce('serverUrl is not configured');
            return false;
        }

        if (!settings.productId) {
            this.warnNotificationsConfigurationOnce('productId is not configured');
            return false;
        }

        if (!settings.firebaseConfig) {
            this.warnNotificationsConfigurationOnce('Firebase is not configured');
            return false;
        }

        if (!settings.vapidKey) {
            this.warnNotificationsConfigurationOnce('VAPID key is not configured');
            return false;
        }

        return true;
    }

    private warnNotificationsConfigurationOnce(detail: string): void {
        if (this.notificationsConfigurationWarningLogged) return;

        console.warn('[Notifications] Push notifications unavailable: ' + detail);
        this.notificationsConfigurationWarningLogged = true;
    }

    private getNotificationsSettings(): any {
        return (environment as any).notifications;
    }

    private getNotificationsProductId(): string {
        const settings = this.getNotificationsSettings();

        return settings ? settings.productId : null;
    }

    private async getCurrentFcmSubscriptionPayload(): Promise<RegisterFcmSubscriptionRequest | null> {
        if (this.currentFcmSubscriptionPayload) {
            return this.currentFcmSubscriptionPayload;
        }

        if (!this.authService || !this.authService.sessionActive()) {
            return null;
        }

        const user = this.authService.getUserSession();
        const document = user && user.information ? user.information.document : null;
        const companyId = user ? user.clientCompanyId : null;
        const roleId = user ? user.roleId : null;
        const token = this.currentFcmToken || await this.getMessagingToken(false);

        if (!document || !companyId || !roleId || !token) {
            return null;
        }

        return {
            companyId,
            productId: this.getNotificationsProductId(),
            document,
            deviceId: this.getOrCreateDeviceId(),
            roleId,
            token
        };
    }

    private canAttemptFcmSubscription(requestBrowserPermission: boolean): boolean {
        const permission = this.getNotificationPermissionState();

        if (permission === 'unsupported') return false;

        if (permission === 'denied') return false;

        return permission === 'granted' || requestBrowserPermission;
    }

    private async getMessagingToken(requestBrowserPermission: boolean = true): Promise<string | null> {
        if (!this.isNotificationsConfigured() || !this.isMessagingSupported()) {
            return null;
        }

        let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

        try {
            const messaging = this.getNotificationMessaging();
            if (!messaging) return null;

            const permission = await this.resolveNotificationPermission(requestBrowserPermission);
            if (permission !== 'granted') {
                return null;
            }

            serviceWorkerRegistration = await this.getFirebaseMessagingServiceWorkerRegistration();
            const restoreFirebaseWarning = this.silenceFirebaseTokenUnsubscribeWarning();
            const token = await messaging.getToken({
                vapidKey: this.getNotificationsSettings().vapidKey,
                serviceWorkerRegistration
            }).then(
                (generatedToken) => {
                    restoreFirebaseWarning();
                    return generatedToken;
                },
                (error) => {
                    restoreFirebaseWarning();
                    throw error;
                }
            );
            try {
                const pushSubscription = serviceWorkerRegistration && serviceWorkerRegistration.pushManager
                    ? await serviceWorkerRegistration.pushManager.getSubscription()
                    : null;
                this.currentBrowserPushEndpoint = pushSubscription ? pushSubscription.endpoint : null;
            } catch (subscriptionError) {
                this.currentBrowserPushEndpoint = null;
            }
            this.clearFcmTokenRecoveryDegradedState();
            this.clearFcmTokenRecoveryRetry();

            return token;
        } catch (error) {
            if (this.isClosingIndexedDbError(error) && serviceWorkerRegistration) {
                const recoveredToken = await this.getMessagingTokenFromServiceWorker(serviceWorkerRegistration);
                if (recoveredToken) {
                    try {
                        const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
                        this.currentBrowserPushEndpoint = subscription ? subscription.endpoint : null;
                    } catch (subscriptionError) {
                        this.currentBrowserPushEndpoint = null;
                    }
                    this.clearFcmTokenRecoveryDegradedState();
                    this.clearFcmTokenRecoveryRetry();
                    return recoveredToken;
                }
            }
            this.lastFcmTokenRecoveryError = error;
            this.recordFcmTokenAcquisitionError(error);
            return null;
        }
    }

    private getMessagingTokenFromServiceWorker(registration: ServiceWorkerRegistration): Promise<string | null> {
        if (!registration || !registration.active || typeof MessageChannel === 'undefined') {
            return Promise.resolve(null);
        }

        return new Promise<string | null>((resolve) => {
            const channel = new MessageChannel();
            let settled = false;
            const timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                resolve(null);
            }, 10000);

            channel.port1.onmessage = (event: MessageEvent) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);

                const response = event.data || {};
                if (response.token) {
                    resolve(response.token);
                    return;
                }

                resolve(null);
            };

            registration.active.postMessage({
                type: this.serviceWorkerFcmTokenRecoveryMessageType,
                vapidKey: this.getNotificationsSettings().vapidKey
            }, [channel.port2]);
        });
    }

    private async resolveNotificationPermission(requestBrowserPermission: boolean): Promise<BrowserNotificationPermissionState> {
        const currentPermission = this.getNotificationPermissionState();

        if (currentPermission !== 'default' || !requestBrowserPermission) {
            return currentPermission;
        }

        return Notification.requestPermission();
    }

    private silenceFirebaseTokenUnsubscribeWarning(): () => void {
        if (typeof console === 'undefined' || !console.warn) {
            return () => { };
        }

        const originalWarn = console.warn;
        const patchedWarn = (...args: any[]) => {
            if (args.some(arg => this.isFirebaseTokenUnsubscribeWarning(arg))) {
                return;
            }

            originalWarn.apply(console, args);
        };
        console.warn = patchedWarn;

        return () => {
            if (console.warn === patchedWarn) {
                console.warn = originalWarn;
            }
        };
    }

    private isFirebaseTokenUnsubscribeWarning(value: any): boolean {
        if (!value) return false;

        const code = value.code ? String(value.code) : '';
        const message = value.message ? String(value.message) : String(value);

        return code.indexOf('messaging/token-unsubscribe-failed') !== -1 ||
            message.indexOf('messaging/token-unsubscribe-failed') !== -1;
    }

    private async getFirebaseMessagingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
        if (this.firebaseMessagingServiceWorkerRegistration) {
            try {
                const cached = await this.firebaseMessagingServiceWorkerRegistration;
                if (cached && cached.active) {
                    return cached;
                }
            } catch (cacheError) {
            }
            this.firebaseMessagingServiceWorkerRegistration = null;
        }

        this.firebaseMessagingServiceWorkerRegistration = this.prepareFirebaseMessagingServiceWorkerRegistration();

        return this.firebaseMessagingServiceWorkerRegistration;
    }

    private async prepareFirebaseMessagingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
        await this.migrateFirebaseMessagingServiceWorkers();

        const registration = await navigator.serviceWorker.register(
            this.firebaseMessagingServiceWorkerPath,
            { scope: this.firebaseMessagingServiceWorkerScope }
        );

        await this.updateFirebaseMessagingServiceWorkerRegistration(registration);
        await this.waitForActiveServiceWorker(registration);

        return registration;
    }

    // Chrome puede dejar la registration sin `active` tras eviction por inactividad, install fallido,
    // o tras unregister externo. `pushManager.subscribe()` requiere SW activado; sin esto, getToken
    // falla con AbortError. Esperamos explícitamente al statechange='activated' del worker en transición.
    private waitForActiveServiceWorker(
        registration: ServiceWorkerRegistration,
        timeoutMs: number = 10000
    ): Promise<void> {
        if (registration.active) return Promise.resolve();

        const candidate = registration.installing || registration.waiting;
        if (!candidate) {
            return Promise.reject(new Error('Service worker has no active/installing/waiting state'));
        }

        return new Promise<void>((resolve, reject) => {
            let settled = false;

            const onStateChange = () => {
                if (settled) return;
                if (candidate.state === 'activated' || registration.active) {
                    settled = true;
                    candidate.removeEventListener('statechange', onStateChange);
                    clearTimeout(timeoutId);
                    resolve();
                } else if (candidate.state === 'redundant') {
                    settled = true;
                    candidate.removeEventListener('statechange', onStateChange);
                    clearTimeout(timeoutId);
                    reject(new Error('Service worker became redundant during activation'));
                }
            };

            const timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                candidate.removeEventListener('statechange', onStateChange);
                reject(new Error('Timeout waiting for service worker to activate'));
            }, timeoutMs);

            candidate.addEventListener('statechange', onStateChange);
        });
    }

    private async migrateFirebaseMessagingServiceWorkers(): Promise<void> {
        if (typeof navigator === 'undefined' ||
            !navigator.serviceWorker ||
            typeof navigator.serviceWorker.getRegistrations !== 'function') {
            return;
        }

        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            const obsoleteRegistrations = registrations.filter(
                registration => this.isObsoleteFirebaseMessagingRegistration(registration)
            );

            for (const registration of obsoleteRegistrations) {
                await registration.unregister();
            }

            this.markFirebaseMessagingSwMigrationCompleted();
        } catch (error) {
            console.warn('[Notifications] Could not migrate Firebase messaging service workers', error);
        }
    }

    private isObsoleteFirebaseMessagingRegistration(registration: ServiceWorkerRegistration): boolean {
        if (!this.isFirebaseMessagingRegistration(registration)) return false;

        const scope = this.normalizeServiceWorkerUrl(registration.scope);
        const script = this.getRegistrationScriptUrl(registration);
        const expectedScope = this.getExpectedFirebaseMessagingScopeUrl();
        const expectedScript = this.getExpectedFirebaseMessagingScriptUrl();

        if (scope !== expectedScope) return true;
        if (script && script !== expectedScript) return true;

        return false;
    }

    private isFirebaseMessagingRegistration(registration: ServiceWorkerRegistration): boolean {
        const scope = this.normalizeServiceWorkerUrl(registration.scope);
        const script = this.getRegistrationScriptUrl(registration);

        return scope.indexOf(this.firebaseMessagingServiceWorkerScope) !== -1 ||
            script.indexOf(this.firebaseMessagingServiceWorkerPath) !== -1 ||
            script.indexOf('firebase-messaging-sw.js') !== -1;
    }

    private async updateFirebaseMessagingServiceWorkerRegistration(registration: ServiceWorkerRegistration): Promise<void> {
        if (!registration || typeof registration.update !== 'function') return;

        try {
            await registration.update();
        } catch (error) {
            // This update is opportunistic; the active worker can continue receiving push.
        }
    }

    private getRegistrationScriptUrl(registration: ServiceWorkerRegistration): string {
        const worker = registration.active || registration.waiting || registration.installing;

        return worker && worker.scriptURL ? this.normalizeServiceWorkerUrl(worker.scriptURL) : '';
    }

    private getExpectedFirebaseMessagingScopeUrl(): string {
        return this.normalizeServiceWorkerUrl(this.firebaseMessagingServiceWorkerScope);
    }

    private getExpectedFirebaseMessagingScriptUrl(): string {
        return this.normalizeServiceWorkerUrl(this.firebaseMessagingServiceWorkerPath);
    }

    private normalizeServiceWorkerUrl(value: string): string {
        if (!value) return '';

        try {
            return new URL(value, window.location.origin).href.replace(/\/$/, '');
        } catch (error) {
            return String(value).replace(/\/$/, '');
        }
    }

    private markFirebaseMessagingSwMigrationCompleted(): void {
        try {
            localStorage.setItem(
                this.firebaseMessagingSwMigrationStorageKey,
                this.firebaseMessagingSwMigrationVersion
            );
        } catch (error) {
            // Storage can be blocked; migration remains safe because detection is based on active registrations.
        }
    }

    private getNotificationMessaging(): firebase.messaging.Messaging | null {
        const settings = this.getNotificationsSettings();
        if (!settings || !settings.firebaseConfig) return null;

        if (!this.notificationMessaging) {
            const app = this.getOrInitializeFirebaseApp(settings.firebaseConfig);
            this.notificationMessaging = firebase.messaging(app);
        }

        return this.notificationMessaging;
    }

    private getOrInitializeFirebaseApp(config: any): firebase.app.App {
        const apps = firebase.apps || [];

        for (let i = 0; i < apps.length; i++) {
            if (apps[i].name === this.notificationsFirebaseAppName) {
                return apps[i];
            }
        }

        return firebase.initializeApp(config, this.notificationsFirebaseAppName);
    }

    private getOrCreateDeviceId(): string {
        if (this.currentNotificationsDeviceId) {
            return this.currentNotificationsDeviceId;
        }

        try {
            const storedDeviceId = localStorage.getItem(this.notificationsDeviceIdStorageKey);
            if (storedDeviceId) {
                this.currentNotificationsDeviceId = storedDeviceId;
                return storedDeviceId;
            }

            const deviceId = this.createDeviceId();
            localStorage.setItem(this.notificationsDeviceIdStorageKey, deviceId);
            this.currentNotificationsDeviceId = deviceId;
            return deviceId;
        } catch (error) {
            this.currentNotificationsDeviceId = this.createDeviceId();
            return this.currentNotificationsDeviceId;
        }
    }

    private createDeviceId(): string {
        const randomSource = window.crypto || (window as any).msCrypto;
        if (randomSource && randomSource.getRandomValues) {
            const values = new Uint32Array(4);
            randomSource.getRandomValues(values);
            return 'web_' + Array.from(values).map(value => value.toString(36)).join('');
        }

        return 'web_' + Date.now().toString(36) + Math.random().toString(36).slice(2);
    }

    private buildFcmSubscriptionKey(payload: RegisterFcmSubscriptionRequest): string {
        return [
            payload.companyId,
            payload.productId,
            payload.document,
            payload.deviceId || '',
            payload.roleId || '',
            payload.token
        ].join('|');
    }

    private logFcmRegistrationResult(response: RegisterFcmSubscriptionResponse): boolean {
        const failures = this.getTopicFailures(response.firebase);
        if (failures.length > 0) {
            console.error('[Notifications] Backend could not subscribe push token to topics', failures);
            return false;
        }

        return true;
    }

    private logFcmUnregistrationResult(response: UnregisterFcmSubscriptionResponse): void {
        const failures = this.getTopicFailures(response.firebase);
        if (failures.length > 0) {
            console.error('[Notifications] Backend could not remove push token from topics on logout', failures);
            return;
        }

    }

    private getTopicFailures(results: TopicSubscriptionResult[]): string[] {
        if (!Array.isArray(results)) {
            return [];
        }

        return results
            .filter(result => result.failureCount > 0)
            .map(result => {
                const reasons = Array.isArray(result.errors)
                    ? result.errors.map(error => error.reason).join('; ')
                    : 'Unknown Firebase error';
                return `${result.topic}: ${reasons}`;
            });
    }

    private getHttpErrorDetail(error: any): any {
        return error && error.error ? error.error : error;
    }

    private registerResolvedFcmTokenWithBackend(token: string): void {
        if (!token || !this.authService || !this.authService.sessionActive()) {
            this.tokenRegistrationActive = false;
            return;
        }

        const user = this.authService.getUserSession();
        const document = user && user.information ? user.information.document : null;
        const companyId = user ? user.clientCompanyId : null;
        const roleId = user ? user.roleId : null;

        if (!document || !companyId || !roleId) {
            console.warn('[Notifications] Missing user data for recovered FCM subscription', { companyId, document, roleId });
            this.tokenRegistrationActive = false;
            return;
        }

        const payload: RegisterFcmSubscriptionRequest = {
            companyId,
            productId: this.getNotificationsProductId(),
            document,
            deviceId: this.getOrCreateDeviceId(),
            roleId,
            token
        };
        const registrationKey = this.buildFcmSubscriptionKey(payload);

        if (this.registeredFcmSubscriptionKey === registrationKey) {
            this.tokenRegistrationActive = false;
            return;
        }

        this.tokenRegistrationActive = true;
        this.attemptRegisterFcmSubscriptionWithBackoff(payload, registrationKey, token, 1);
    }

    private attemptRegisterFcmSubscriptionWithBackoff(
        payload: RegisterFcmSubscriptionRequest,
        registrationKey: string,
        token: string,
        attempt: number
    ): void {
        this.notificationsClient.registerFcmSubscription(payload).subscribe(
            (response) => {
                const registered = this.logFcmRegistrationResult(response);
                if (registered) {
                    this.currentFcmToken = token;
                    this.currentFcmSubscriptionPayload = payload;
                    this.registeredFcmSubscriptionKey = registrationKey;
                    this.lastFcmSubscriptionRegisteredAt = Date.now();
                }
                this.tokenRegistrationActive = false;
            },
            (error) => {
                const transient = this.isTransientHttpError(error);
                const shouldRetry = transient && attempt < this.fcmRegisterMaxAttempts;

                if (!shouldRetry) {
                    console.error('[Notifications] Failed to register push token with backend', {
                        attempt,
                        error: this.getHttpErrorDetail(error)
                    });
                    this.tokenRegistrationActive = false;
                    return;
                }

                const delay = this.computeFcmRegisterBackoffDelay(attempt);
                this.fcmRegisterRetryTimeoutId = setTimeout(() => {
                    this.fcmRegisterRetryTimeoutId = null;
                    this.attemptRegisterFcmSubscriptionWithBackoff(payload, registrationKey, token, attempt + 1);
                }, delay);
            }
        );
    }

    private computeFcmRegisterBackoffDelay(attempt: number): number {
        const exponential = this.fcmRegisterBaseDelayMs * Math.pow(2, Math.max(0, attempt - 1));
        const jitter = Math.floor(Math.random() * 500);
        return Math.min(exponential + jitter, this.fcmRegisterMaxDelayMs);
    }

    private scheduleFcmTokenRecoveryRetry(reason?: string): void {
        if (!this.canScheduleFcmTokenRecoveryRetry()) {
            return;
        }

        if (this.fcmTokenRecoveryRetryTimeoutId) {
            return;
        }

        if (this.fcmTokenRecoveryAttempt >= this.fcmTokenRecoveryMaxAttempts) {
            console.warn('[Notifications] Unable to restore push token after retries', {
                reason,
                attempts: this.fcmTokenRecoveryAttempt,
                error: this.lastFcmTokenRecoveryError
            });
            this.fcmTokenRecoveryAttempt = 0;
            return;
        }

        this.fcmTokenRecoveryAttempt++;
        const delay = this.computeFcmTokenRecoveryBackoffDelay(this.fcmTokenRecoveryAttempt);

        this.fcmTokenRecoveryRetryTimeoutId = setTimeout(() => {
            this.fcmTokenRecoveryRetryTimeoutId = null;

            if (!this.canScheduleFcmTokenRecoveryRetry()) {
                return;
            }

            this.revalidateFcmToken(reason || 'token-retry');
        }, delay);
    }

    private clearFcmTokenRecoveryRetry(): void {
        if (this.fcmTokenRecoveryRetryTimeoutId) {
            clearTimeout(this.fcmTokenRecoveryRetryTimeoutId);
            this.fcmTokenRecoveryRetryTimeoutId = null;
        }
        this.fcmTokenRecoveryAttempt = 0;
        this.lastFcmTokenRecoveryError = null;
    }

    private canScheduleFcmTokenRecoveryRetry(): boolean {
        if (!this.authService || !this.authService.sessionActive()) return false;
        if (this.getNotificationPermissionState() !== 'granted') return false;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
        if (this.isFcmTokenRecoveryPaused()) return false;

        return true;
    }

    private computeFcmTokenRecoveryBackoffDelay(attempt: number): number {
        const exponential = this.fcmTokenRecoveryBaseDelayMs * Math.pow(2, Math.max(0, attempt - 1));
        const jitter = Math.floor(Math.random() * 750);
        return Math.min(exponential + jitter, this.fcmTokenRecoveryMaxDelayMs);
    }

    private isTransientHttpError(error: any): boolean {
        if (!error) return true;
        const status = typeof error.status === 'number' ? error.status : null;
        if (status === null) return true;
        if (status === 0) return true;
        if (status === 408 || status === 429) return true;
        if (status >= 500 && status < 600) return true;
        return false;
    }

    public async revalidateFcmToken(reason?: string): Promise<void> {
        if (!this.authService || !this.authService.sessionActive()) {
            return;
        }
        if (this.tokenRegistrationActive) {
            return;
        }
        if (this.isFcmTokenRecoveryPaused()) return;

        const forceReasons = ['heartbeat-stale', 'heartbeat-no-token', 'push-subscription-missing', 'push-subscription-changed'];
        const shouldForce = !!reason && forceReasons.indexOf(reason) !== -1;

        try {
            const newToken = await this.getMessagingToken(false);

            if (!newToken) {
                this.registeredFcmSubscriptionKey = null;
                this.scheduleFcmTokenRecoveryRetry(reason);
                return;
            }

            if (newToken !== this.currentFcmToken) {
                this.registeredFcmSubscriptionKey = null;
                this.registerResolvedFcmTokenWithBackend(newToken);
                return;
            }

            if (shouldForce) {
                this.registeredFcmSubscriptionKey = null;
                this.registerResolvedFcmTokenWithBackend(newToken);
                return;
            }

        } catch (error) {
            this.lastFcmTokenRecoveryError = error;
            this.registeredFcmSubscriptionKey = null;
            this.scheduleFcmTokenRecoveryRetry(reason);
        }
    }

    public async recoverPushDeliveryChannel(reason?: string): Promise<void> {
        if (!this.authService || !this.authService.sessionActive()) return;
        if (this.getNotificationPermissionState() !== 'granted') return;

        try {
            const registration = await this.getFirebaseMessagingServiceWorkerRegistration();
            const subscription = registration && registration.pushManager
                ? await registration.pushManager.getSubscription()
                : null;

            if (subscription) {
                if (!this.currentFcmToken) {
                    await this.revalidateFcmToken(reason || 'push-channel-no-session-token');
                    return;
                }

                if (this.currentBrowserPushEndpoint && this.currentBrowserPushEndpoint !== subscription.endpoint) {
                    this.registeredFcmSubscriptionKey = null;
                    await this.revalidateFcmToken('push-subscription-changed');
                    return;
                }

                this.currentBrowserPushEndpoint = subscription.endpoint;
                return;
            }

            this.registeredFcmSubscriptionKey = null;
            await this.revalidateFcmToken('push-subscription-missing');
        } catch (error) {
            console.warn('[Notifications] Unable to restore browser push channel; inbox fallback remains active', {
                reason,
                error
            });
        }
    }

    private recordFcmTokenAcquisitionError(error: any): void {
        if (!this.isClosingIndexedDbError(error)) {
            this.fcmIndexedDbClosingFailureCount = 0;
            return;
        }

        this.fcmIndexedDbClosingFailureCount++;
        if (this.fcmIndexedDbClosingFailureCount < this.fcmIndexedDbClosingFailureThreshold) {
            return;
        }

        this.fcmTokenRecoveryPausedUntil = Date.now() + this.fcmTokenRecoveryPauseMs;
        this.clearFcmTokenRecoveryRetry();
        console.warn('[Notifications] FCM token recovery degraded: IndexedDB remains closing; pausing token probes while inbox synchronization remains available', {
            pauseMs: this.fcmTokenRecoveryPauseMs,
            failures: this.fcmIndexedDbClosingFailureCount
        });
    }

    private isClosingIndexedDbError(error: any): boolean {
        if (!error) return false;

        const name = error.name ? String(error.name) : '';
        const message = error.message ? String(error.message) : String(error);

        return name === 'InvalidStateError' &&
            message.indexOf('IDBDatabase') !== -1 &&
            message.toLowerCase().indexOf('connection is closing') !== -1;
    }

    private isFcmTokenRecoveryPaused(): boolean {
        if (!this.fcmTokenRecoveryPausedUntil) return false;
        if (Date.now() < this.fcmTokenRecoveryPausedUntil) return true;

        this.fcmTokenRecoveryPausedUntil = 0;
        this.fcmIndexedDbClosingFailureCount = 0;
        return false;
    }

    private clearFcmTokenRecoveryDegradedState(): void {
        this.fcmIndexedDbClosingFailureCount = 0;
        this.fcmTokenRecoveryPausedUntil = 0;
    }

    public getCurrentFcmToken(): string | null {
        return this.currentFcmToken || null;
    }

    public getCurrentFcmSubscriptionPayloadSnapshot(): RegisterFcmSubscriptionRequest | null {
        return this.currentFcmSubscriptionPayload || null;
    }

    public getLastFcmSubscriptionRegisteredAt(): number {
        return this.lastFcmSubscriptionRegisteredAt;
    }

}
